import React, { useId, useState } from 'react';
import styled from 'styled-components';
import {
  HEATMAP_RAMP,
  inkOn,
  radius,
  rgba,
  semantic,
  shadow,
  space,
  typeRole,
  weight,
} from '@repo/design-tokens';
import type { HeatGridWindow } from '@repo/frontend-types';
import { Button, Icon, Kbd, Select, Slider, Switch } from '@repo/design-system';
import type { SelectOption } from '@repo/design-system';
import { StepGuide, StepHint, visuallyHidden } from '@repo/ui';
import type { StepGuideModel } from '@repo/ui';

import { MovableCard } from './observerWorkspace/MovableCard';
import { useCardPositions } from './observerWorkspace/cardPositions';

/**
 * The heat history's one choice, its window (`observer-autogen-heat-history-view`, R8; card 25).
 * The body part is not chosen here: it follows the tracking card (`observers-heat-follows-tracked-part`,
 * R1, R3), and Live is retired (R4).
 */
export interface HeatHistoryChoices {
  window: HeatGridWindow;
  onWindowChange: (next: HeatGridWindow) => void;
}

/** The heat window: the four history windows. Suggestions read the same window. */
export const WINDOW_OPTIONS: SelectOption[] = [
  { value: '1d', label: '1 day' },
  { value: '7d', label: '7 days' },
  { value: '14d', label: '14 days' },
  { value: '28d', label: '28 days' },
];

export interface CameraLayers {
  /** Other entities' regions on this camera, as the dashed backdrop. */
  observers: {
    count: number;
    cameraName: string;
    visible: boolean;
    onChange: (on: boolean) => void;
  };
  /**
   * Detection heat; `null` when this camera has no stream to read it for. `history`, when given,
   * adds the window and body-part choices to the toolbar.
   */
  heat: {
    description: string;
    visible: boolean;
    onChange: (on: boolean) => void;
    history?: HeatHistoryChoices;
    /** The *Heat outlines* switch and its *Outline edge* slider, when the step offers them. */
    outlines?: HeatOutlineChoices;
  } | null;
  /**
   * The engine's suggested observers (`observer-autogen-review-ui-preview`, R1, R3, R6): the
   * *Suggestions* switch, and — while it is on — the status line and the camera's warnings in the
   * status strip. They read the heat window (`observer-autogen-review-layout`, L5.2). Omitted where
   * the camera has no stream or the step has no saved plan.
   */
  suggestions?: SuggestionLayer | null;
}

/**
 * *Heat outlines* (`observer-matching-beyond-geometry-heat-outlines`, R1, R2): outlines round the
 * blobs of heat, and the share of the hottest heat their edge sits at. Owned by the step.
 */
export interface HeatOutlineChoices {
  on: boolean;
  onChange: (on: boolean) => void;
  /** 5 to 95, in steps of 5. */
  edge: number;
  /** Every step of a drag: the outlines follow live. */
  onEdgeChange: (edge: number) => void;
  /** Once a drag or key press has finished: when the count is announced. */
  onEdgeCommit: (edge: number) => void;
  /**
   * *Separate peaks* (`observer-matching-beyond-geometry-region-split`, R3): split an outline where
   * the heat dips between peaks, one outline per machine.
   */
  separatePeaks: boolean;
  onSeparatePeaksChange: (on: boolean) => void;
  /** Why the switch cannot be used yet (heat loading or failed); omitted when it can. */
  unavailable?: string;
}

/** The read-only Suggestions layer's controls and words (all owned by the step). */
export interface SuggestionLayer {
  visible: boolean;
  onChange: (on: boolean) => void;
  description: string;
  /** The status line, said through a polite live region. */
  status: string;
  warnings: string[];
  /** Retry after a failure, Refresh after an answer; none while loading. */
  action: { kind: 'retry' | 'refresh'; onClick: () => void } | null;
}

/** The region being drawn: its point count and the bar's two actions. */
export interface CameraDrawing {
  /** Points in the open region; 0 when none is open. */
  points: number;
  onUndoPoint: () => void;
  onCancel: () => void;
}

/** The camera on the toolbar: its name, when the picture was taken, and Plan. */
export interface CameraTitle {
  name: string;
  pictureTime: string | null;
  onPlan: () => void;
}

/**
 * The list panel's fold, on the toolbar (`onboarding-persistent-scene-observers-glass`, R2.7). Full
 * screen is gone: the plan already fills the page under the glass.
 */
export interface CameraSpace {
  listOpen: boolean;
  onToggleList: () => void;
}

/** Plan's name on the toolbar — the stage head's old button, the same words (`ObserverStage`). */
export const PLAN_BUTTON_LABEL = 'Put the plan back on stage';

/** A movable card over the picture, given the grip it places in its own header (`MovableCard`). */
export type CardRender = (grip: React.ReactNode) => React.ReactNode;

/** Said by every grip: how to move a card from the keyboard (FR4). */
export const GRIP_DESCRIPTION = 'Arrow keys move it. Home puts it back.';

const noAnnouncer = () => undefined;

/**
 * A layer switch on the toolbar: its label only, so the row stays one line. What the label's second
 * line used to say ("14 on Cardio", "Feet · last 14 days") is its accessible description and its
 * tooltip (`observer-autogen-review-layout`, L3.2).
 */
const LayerSwitch: React.FC<{
  id: string;
  description: string;
  checked: boolean;
  onChange: (on: boolean) => void;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ id, description, checked, onChange, disabled, children }) => (
  <span title={description}>
    <Switch
      surface="on-glass"
      hue="peri"
      checked={checked}
      disabled={disabled}
      aria-describedby={id}
      onChange={e => onChange(e.currentTarget.checked)}
    >
      {children}
    </Switch>
    <span id={id} style={visuallyHidden}>
      {description}
    </span>
  </span>
);

/**
 * One camera on the stage (`observers-page-camera-stage`, FR3–FR6, FR26; laid out by
 * `observer-autogen-review-layout`): ONE toolbar row (the camera, layers, heat window, Plan,
 * and the list panel's fold; there is no mode, `observer-unified-draw-edit`), then the picture taking the rest of the height. The step
 * guide, the hint, the Undo bar, the camera's messages and the suggestions' status ride a status
 * strip overlaid at the picture's foot, so nothing above the picture reserves height for them.
 * Over the picture sit its cards — the entity card, the tracking card,
 * the dock and the corner plan, each movable by its grip and remembered per person
 * (`observers-page-motion`, `MovableCard`), with homes top-left, bottom-left, bottom-right and
 * top-right. The shortcut card stays bottom-right, above the dock's home. The corner plan is the
 * stage's own (`ObserverStage`), portalled into the slot left for it (`data-camera-corner`); the
 * toasts sit at the bottom centre of the window (`onboarding-persistent-scene-observers-glass`, R6).
 *
 * Presentational: the step owns every piece of state and builds the drawing area, so this only lays
 * them out. Each card is marked `data-control`, so a press or a key on it never reaches the canvas.
 * `cardsSub` is the signed-in person (Auth0 `sub`) whose card layout this reads and writes, here and
 * not in the step, so a committed move re-renders this view rather than the whole step. Without it,
 * moves last while this is mounted.
 */
export const ObserverCameraView: React.FC<{
  guide: StepGuideModel;
  layers: CameraLayers;
  banners?: React.ReactNode;
  picture: React.ReactNode;
  entityCard?: CardRender;
  trackingCard?: CardRender;
  dock?: CardRender;
  shortcutCard?: React.ReactNode;
  cardsSub?: string;
  /**
   * Says a sentence through the stage's ONE polite region (`ObserverStage`'s `note`) — a card sent
   * home (A4). The cards keep no region of their own: the stage has exactly one.
   */
  announce?: (text: string) => void;
  /**
   * The open region, if any: while it has points, the Undo bar leads the hint row (Observers page
   * v2, state 03 note 5) — beside the hint, where people look while drawing.
   */
  drawing?: CameraDrawing | null;
  /** The camera's name, picture time and Plan, on the toolbar. Omitted in isolated mounts. */
  camera?: CameraTitle | null;
  /** The list panel's fold, on the toolbar. Omitted in isolated mounts. */
  space?: CameraSpace | null;
}> = ({
  guide,
  layers,
  banners,
  picture,
  entityCard,
  trackingCard,
  dock,
  shortcutCard,
  cardsSub,
  announce: announceProp,
  drawing,
  camera,
  space: spaceControls,
}) => {
  const positions = useCardPositions(cardsSub);
  const [layer, setLayer] = useState<HTMLDivElement | null>(null);
  const [outlineHost, setOutlineHost] = useState<HTMLDivElement | null>(null);
  const describedById = `${useId()}-grip-help`;
  const descId = useId();

  const announce = announceProp ?? noAnnouncer;
  // The shortcut card stays put above the dock's HOME while the dock moves (FR1): its offset is the
  // dock's measured height.
  const [dockHeight, setDockHeight] = useState(0);

  const card = {
    layer,
    outlineHost,
    positions,
    describedById,
    announce,
  };

  return (
    <Root>
      <Toolbar data-camera-toolbar="">
        {camera && (
          <CameraName>
            {/* The stage's heading on the camera view, as the stage head's title was. */}
            <h3 className="name" title={camera.name}>
              {camera.name}
            </h3>
            {camera.pictureTime && (
              <span className="time" title={`Picture taken ${camera.pictureTime}`}>
                <Icon name="image" size="xs" />
                <span style={visuallyHidden}>Picture taken </span>
                {camera.pictureTime}
              </span>
            )}
          </CameraName>
        )}
        <Layers>
          <LayerSwitch
            id={`${descId}-observers`}
            description={`${layers.observers.count} on ${layers.observers.cameraName}`}
            checked={layers.observers.visible}
            onChange={on => layers.observers.onChange(on)}
          >
            Observers
          </LayerSwitch>
          {layers.heat && (
            <LayerSwitch
              id={`${descId}-heat`}
              description={layers.heat.description}
              checked={layers.heat.visible}
              onChange={on => layers.heat?.onChange(on)}
            >
              Heatmap
            </LayerSwitch>
          )}
          {layers.heat?.outlines && (
            <LayerSwitch
              id={`${descId}-heat-outlines`}
              description={
                !layers.heat.visible
                  ? 'Turn on Heatmap to outline its blobs'
                  : (layers.heat.outlines.unavailable ??
                    'Outlines each blob of heat; click one to add it as a region')
              }
              checked={layers.heat.outlines.on}
              disabled={!layers.heat.visible || !!layers.heat.outlines.unavailable}
              onChange={on => layers.heat?.outlines?.onChange(on)}
            >
              Heat outlines
            </LayerSwitch>
          )}
          {layers.suggestions && (
            <LayerSwitch
              id={`${descId}-suggestions`}
              description={layers.suggestions.description}
              checked={layers.suggestions.visible}
              onChange={on => layers.suggestions?.onChange(on)}
            >
              Suggestions
            </LayerSwitch>
          )}
        </Layers>
        {layers.heat?.history && (
          // `data-control`: an open list's Esc closes the list, never the drawing area's open region.
          <Choices data-heat-choices="" data-control="">
            <Select
              size="compact"
              ariaLabel="Heat window"
              options={WINDOW_OPTIONS}
              value={layers.heat.history.window}
              onChange={(next: string) =>
                layers.heat?.history?.onWindowChange(next as HeatGridWindow)
              }
            />
          </Choices>
        )}
        <Trailing>
          {camera && (
            <Button
              type="button"
              variant="text"
              size="xs"
              aria-label={PLAN_BUTTON_LABEL}
              iconLeft={<Icon name="map" />}
              onClick={camera.onPlan}
            >
              Plan <Kbd keys="P" />
            </Button>
          )}
          {spaceControls && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              iconOnly
              aria-label={
                spaceControls.listOpen
                  ? 'Fold the inventory list ([)'
                  : 'Open the inventory list ([)'
              }
              title={
                spaceControls.listOpen
                  ? 'Fold the inventory list ([)'
                  : 'Open the inventory list ([)'
              }
              aria-pressed={spaceControls.listOpen}
              aria-keyshortcuts="["
              iconLeft={<Icon name="rectangle-list" />}
              onClick={spaceControls.onToggleList}
            />
          )}
        </Trailing>
      </Toolbar>
      {layers.heat?.outlines?.on && layers.heat.visible && !layers.heat.outlines.unavailable && (
        <OutlineSettings data-outline-settings="">
          <LayerSwitch
            id={`${descId}-heat-outline-peaks`}
            description="Splits an outline where the heat dips between peaks, one outline per machine"
            checked={layers.heat.outlines.separatePeaks}
            onChange={on => layers.heat?.outlines?.onSeparatePeaksChange(on)}
          >
            Separate peaks
          </LayerSwitch>
          {/* `data-control`: the slider's arrows and Home/End never reach the drawing area. */}
          <OutlineEdge data-control="">
            <Slider
              surface="on-glass"
              size="sm"
              label="Outline edge"
              value={layers.heat.outlines.edge}
              min={5}
              max={95}
              step={5}
              format={value => `${value}\u2009%`}
              onChange={value => layers.heat?.outlines?.onEdgeChange(value)}
              onCommit={value => layers.heat?.outlines?.onEdgeCommit(value)}
            />
          </OutlineEdge>
        </OutlineSettings>
      )}
      <Picture data-camera-picture="">
        {picture}
        <Cards ref={setLayer}>
          {/* Home outlines first, so every card paints over them (FR5). */}
          <div className="outlines" ref={setOutlineHost} />
          {entityCard && (
            <MovableCard id="entity" home="top-left" name="entity card" {...card}>
              {entityCard}
            </MovableCard>
          )}
          {trackingCard && (
            <MovableCard id="tracking" home="bottom-left" name="body-part card" {...card}>
              {trackingCard}
            </MovableCard>
          )}
          {/* The shortcut card stays bottom-right, above the dock's home, wherever the dock is moved
            (FR1, the design of record). */}
          {shortcutCard && (
            <div
              className="shortcuts"
              style={{ bottom: `calc(${space[3]} + ${space[2]} + ${dockHeight}px)` }}
            >
              {shortcutCard}
            </div>
          )}
          {dock && (
            <MovableCard
              id="dock"
              home="bottom-right"
              name="picture toolbar"
              onSize={size => setDockHeight(size.h)}
              {...card}
            >
              {dock}
            </MovableCard>
          )}
          {/* The stage portals its corner plan here (v3: top-right of the picture). Last in the DOM,
            so the Tab order ends on it after the dock, as Block A lists. The grip is the slot's
            own, because the plan arrives by portal rather than as a child. */}
          <MovableCard
            id="plan"
            home="top-right"
            name="plan"
            gripPlacement="corner"
            // Resized from a corner after a short rest there (`observers-corner-plan-resize`).
            resizable
            portalTargetProps={{ 'data-camera-corner': '' }}
            {...card}
          >
            {() => null}
          </MovableCard>
          <span id={describedById} style={visuallyHidden}>
            {GRIP_DESCRIPTION}
          </span>
        </Cards>
      </Picture>
      {/* The status strip (`observer-autogen-review-layout`, L3.3): the guide, the hint, the Undo
          bar, the suggestions' status and the camera's messages. Docked UNDER the picture, on one
          line (`observer-autogen-review-walkthrough`): overlaid at the picture's foot it covered
          points near the bottom edge while drawing, and its first pill wrapped at ~1568 px. It is a
          control surface: presses on it never reach the canvas. */}
      <StatusStrip data-control="" data-testid="camera-status-strip">
        <div className="lead">
          {drawing && drawing.points > 0 && (
            <UndoBar>
              <span className="points">
                {drawing.points} point{drawing.points === 1 ? '' : 's'}
              </span>
              <span className="bar-divider" aria-hidden="true" />
              <Button
                type="button"
                variant="ghost"
                size="xs"
                iconLeft={<Icon name="rotate-left" />}
                onClick={drawing.onUndoPoint}
                // The caps are shown, not named: the button is "Undo Point" (Block A3).
                aria-keyshortcuts="Control+Z"
              >
                Undo Point{' '}
                <span aria-hidden="true">
                  <Kbd keys={['Ctrl', 'Z']} />
                </span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={drawing.onCancel}
                aria-keyshortcuts="Escape"
              >
                Cancel{' '}
                <span aria-hidden="true">
                  <Kbd keys="Esc" />
                </span>
              </Button>
            </UndoBar>
          )}
          <StepGuide guide={guide} />
          <StepHint hint={guide.hint} />
          {layers.heat?.history && layers.heat.visible && (
            <HeatKey aria-hidden="true">
              Fewer visits
              <i />
              More
            </HeatKey>
          )}
        </div>
        {layers.suggestions?.visible && (
          <div className="suggestions">
            <SuggestionStatus>
              <span role="status" aria-live="polite" data-suggestion-status="">
                {layers.suggestions.status}
              </span>
              {layers.suggestions.action && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={layers.suggestions.action.onClick}
                  aria-label={
                    layers.suggestions.action.kind === 'retry'
                      ? 'Retry suggestions'
                      : 'Refresh suggestions'
                  }
                >
                  {layers.suggestions.action.kind === 'retry' ? 'Retry' : 'Refresh'}
                </Button>
              )}
            </SuggestionStatus>
            {layers.suggestions.warnings.length > 0 && (
              <SuggestionWarnings data-suggestion-warnings="">
                {/* Index keys: two warnings may read the same (a detail repeating a code's sentence). */}
                {layers.suggestions.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </SuggestionWarnings>
            )}
          </div>
        )}
        {banners}
      </StatusStrip>
    </Root>
  );
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
  min-height: 0;
  flex: 1;
  padding: ${space[1]} ${space[3]} ${space[3]};
`;

/** The camera's ONE control row (`observer-autogen-review-layout`, L3.2): it wraps only when narrow. */
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]} ${space[3]};
  min-height: 36px;
`;

/** The camera's name and when its picture was taken. */
const CameraName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  min-width: 0;
  max-width: 260px;
  .name {
    margin: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: ${typeRole.body1Stronger.fontSize};
    line-height: ${typeRole.body1Stronger.lineHeight};
    font-weight: ${typeRole.body1Stronger.fontWeight};
    color: ${semantic.text.primary};
  }
  .time {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: ${space[1]};
    font-size: ${typeRole.caption1.fontSize};
    color: ${semantic.text.muted};
  }
`;

/** The heat window: a compact select (the body part follows the tracking card). */
const Choices = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  > * {
    width: 112px;
  }
`;

/** Plan and the list panel's fold, at the row's end. */
const Trailing = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  margin-left: auto;
`;

/**
 * The status strip, docked under the picture on the same glass as the dock
 * (`observer-autogen-review-walkthrough`): it never covers the picture, and its lead row stays on one
 * line — the step pills never wrap, and the hint ends in an ellipsis. Warnings and messages, when
 * there are any, add rows beneath.
 */
const StatusStrip = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  padding: ${space[1]} ${space[3]};
  border-radius: ${radius.lg};
  background: ${rgba(semantic.surface.base, 0.82)};
  box-shadow: ${shadow.glassSm};
  .lead {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: ${space[3]};
    min-width: 0;
    overflow: hidden;
  }
  .lead > ol,
  .lead > ol li {
    flex: none;
    white-space: nowrap;
  }
  /* One line: the hint ends in an ellipsis rather than growing the strip (StepHint's .hint-text). */
  .lead > p {
    flex: 1;
    min-width: 0;
  }
  .suggestions {
    display: flex;
    flex-direction: column;
    gap: ${space[1]};
  }
`;

/** Undo Point while a region is open, leading the hint row (v2, state 03 note 5). */
const UndoBar = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: ${space[2]};
  padding: 3px 3px 3px ${space[3]};
  border-radius: ${radius.md};
  background: ${rgba(semantic.surface.base, 0.75)};
  box-shadow: 0 0 10px 2px ${rgba(semantic.status.roles.beingEdited.mark, 0.3)};
  .points {
    font-size: ${typeRole.caption1.fontSize};
    font-weight: ${weight.semibold};
    color: ${inkOn.aqua};
  }
  .bar-divider {
    width: 1px;
    align-self: stretch;
    background: ${semantic.border.faint};
  }
`;

/*
 * The Switch sets its own label `space[3]` from its track, so the switches sit twice that apart:
 * each label then reads as its own track's, not the next one's (`observers-heat-follows-tracked-part`, R5).
 */
const Layers = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${space[6]};
`;

/**
 * The heat outlines' own settings, a row under the toolbar while they are on
 * (`observer-matching-beyond-geometry-region-split-tidy`, R3): with *Separate peaks* the toolbar no
 * longer fits one row, so these two sit below it rather than wrapping the whole switch group.
 */
const OutlineSettings = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${space[2]} ${space[6]};
`;

/** The *Outline edge* slider beside its switch: wide enough to read its value and drag finely. */
const OutlineEdge = styled.span`
  display: inline-flex;
  width: calc(${space[12]} * 4);
`;

/** The suggestions' status line and its Retry / Refresh. */
const SuggestionStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  font-size: ${typeRole.caption1.fontSize};
  color: ${semantic.text.body};
`;

/** Why a camera has fewer or no suggestions, one plain sentence each. */
const SuggestionWarnings = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: ${space[1]} ${space[3]};
  font-size: ${typeRole.caption2.fontSize};
  color: ${semantic.text.muted};
`;

/** "Fewer visits → More" over the heat ramp's stops, the quietest one faint (card 25). */
const HeatKey = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[2]};
  font-size: ${typeRole.caption2.fontSize};
  color: ${semantic.text.body};
  i {
    display: block;
    width: 96px;
    height: 8px;
    border-radius: ${radius.pill};
    background: linear-gradient(
      90deg,
      ${rgba(HEATMAP_RAMP[0], 0.15)} 0%,
      ${HEATMAP_RAMP.map(
        (hex, i) => `${hex} ${Math.round(8 + (i * 92) / (HEATMAP_RAMP.length - 1))}%`,
      ).join(', ')}
    );
  }
`;

const Picture = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* The cards over the picture. The layer passes pointers through; each card's slot takes them back
   (`MovableCard`), and the slots place themselves. */
const Cards = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  .shortcuts {
    position: absolute;
    right: ${space[3]};
  }
`;
